import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { PropsWithChildren, ReactNode, Ref } from 'react';
import { useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import type { Props as ButtonProps } from '@sorare/core/src/atoms/buttons/Button';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { Dropdown } from '@sorare/core/src/atoms/dropdowns/Dropdown';
import { ConditionalWrapper } from '@sorare/core/src/atoms/layout/ConditionalWrapper';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';

import { AddToWatchlist } from '#src/components/watchlists/AddToWatchlist/index.tsx';

import type { AddToWatchlistButton_anyPlayer } from './__generated__/index.graphql.ts';

type Props = {
  anyPlayer: AddToWatchlistButton_anyPlayer;
  variant?: 'icon' | 'default';
  ctaLabel?: ReactNode;
  container?: true | null;
} & ButtonProps;

const RefWrapper = ({
  children,
  ref,
}: PropsWithChildren<{
  ref?: Ref<HTMLSpanElement>;
}>) => {
  return (
    <Vertical gap={0}>
      <span ref={ref} />
      {children}
    </Vertical>
  );
};

export const AddToWatchlistButton = ({
  anyPlayer,
  variant = 'default',
  ctaLabel,
  fullWidth,
  startIcon,
  container,
  ...props
}: Props) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLSpanElement>(null);

  if (!anyPlayer.printablePlayer) return null;
  return (
    <ConditionalWrapper
      wrap={!!container}
      Wrapper={RefWrapper}
      props={{
        ref: containerRef,
      }}
    >
      <Dropdown
        gap={8}
        open={open}
        onClose={() => {
          setOpen(false);
        }}
        // oxlint-disable-next-line react/refs
        container={container ? containerRef.current : null}
        label={p =>
          variant === 'icon' ? (
            <IconButton {...props} {...p} onClick={() => setOpen(true)}>
              {startIcon}
            </IconButton>
          ) : (
            <Button
              style={{ ...(!fullWidth && { flexShrink: 0 }) }}
              fullWidth={fullWidth}
              startIcon={startIcon}
              {...props}
              {...p}
              onClick={() => setOpen(true)}
            >
              {ctaLabel || <FormattedMessage defaultMessage="Add to" />}
            </Button>
          )
        }
        align="right"
      >
        <AddToWatchlist anyPlayer={anyPlayer} onClose={() => setOpen(false)} />
      </Dropdown>
    </ConditionalWrapper>
  );
};
AddToWatchlistButton.fragments = {
  anyPlayer: gql`
    fragment AddToWatchlistButton_anyPlayer on AnyPlayerInterface {
      slug
      printablePlayer {
        id
      }
      ...AddToWatchlist_anyPlayer
    }
    ${AddToWatchlist.fragments.anyPlayer}
  ` as TypedDocumentNode<AddToWatchlistButton_anyPlayer>,
};
